import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
} from '@mui/material';
import { useReportConfig } from '../../contexts/ReportConfigContext.jsx';
import ReportStyledField from './ReportStyledField.jsx';
import { formatDateBr as formatDateBrGlobal } from '../../utils/formatters/dateUtils';
import { classifyReservaLegalStatus } from '../../utils/reportCalculations';
import ReportWidget from './ReportWidget';

const safeNumber = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

const statusMap = {
  AT: 'Ativo',
  PE: 'Pendente',
  CA: 'Cancelado',
};

const tipoImovelMap = {
  IRU: 'Imóvel Rural',
  AST: 'Assentamento INCRA',
  PCT: 'Povos e Comunidades Tradicionais',
};

const formatStatusLabel = (code) => {
  if (!code) return '-';
  const label = statusMap[code];
  if (label) return label;
  return code;
};

const formatTipoImovelLabel = (code) => {
  if (!code) return '-';
  const label = tipoImovelMap[code];
  if (label) return label;
  return code;
};

const getStatusChipProps = (code) => {
  const label = formatStatusLabel(code);
  switch (code) {
    case 'AT':
      return { label, color: 'success' };
    case 'PE':
      return { label, color: 'warning' };
    case 'CA':
      return { label, color: 'error' };
    default:
      return { label: label || '-', color: 'default' };
  }
};

const formatArea = (value) => {
  const n = safeNumber(value);
  if (n === null) return '';
  return n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const computePercent = (areaTema, areaPropriedade) => {
  const tema = safeNumber(areaTema);
  const total = safeNumber(areaPropriedade);
  if (tema === null || total === null || total === 0) return '';
  const pct = (tema / total) * 100;
  return `${pct.toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} %`;
};

const formatDateBr = (value) => {
  if (value === null || value === undefined || value === '') return '-';
  const formatted = formatDateBrGlobal(value);
  return formatted || String(value);
};

const formatPercentValue = (value) => {
  const n = safeNumber(value);
  if (n === null) return '';
  return `${n.toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} %`;
};

const formatBiomaChipLabel = (bioma, pct) => {
  if (!bioma) return '-';
  const hasPct = pct !== null && pct !== undefined && pct !== '';
  if (!hasPct) return bioma;
  return `${bioma} (${formatPercentValue(pct)})`;
};

const extractBiomas = (sobreposicaoData) => {
  if (!sobreposicaoData) return [];

  const array = Array.isArray(sobreposicaoData) ? sobreposicaoData : [sobreposicaoData];

  const collected = [];

  array.forEach((item) => {
    const biomas = Array.isArray(item?.temas?.biomas) ? item.temas.biomas : [];
    biomas.forEach((b) => {
      const nome = b?.bioma;
      const pct = b?.pct_imovel;
      if (nome) {
        collected.push({ bioma: nome, pct });
      }
    });
  });

  const map = new Map();
  collected.forEach((b) => {
    const key = `${b.bioma}-${b.pct ?? ''}`;
    if (!map.has(key)) {
      map.set(key, b);
    }
  });

  // Garantir no máximo 3 biomas distintos
  return Array.from(map.values()).slice(0, 3);
};

const leftThemes = [
  { label: 'Área Líquida', field: 'area_liquida' },
  { label: 'Área de Preservação Permanente', field: 'area_de_preservacao_permanente' },
  { label: 'Área de Reserva Legal Proposta', field: 'area_de_reserva_legal_proposta' },
  { label: 'Área de Reserva Legal Averbada', field: 'area_de_reserva_legal_averbada' },
  {
    label: 'Área de Reserva Legal não Averbada',
    field: 'area_de_reserva_legal_aprovada_e_nao_averbada',
  },
  { label: 'Área de Vegetação Nativa', field: 'area_remanescente_de_vegetacao_nativa' },
  { label: 'Área de Uso Restrito', field: 'area_de_uso_restrito' },
];

const rightThemes = [
  { label: 'Área Não Classificada', field: 'area_nao_classificada' },
  { label: 'Área de Servidão Administrativa', field: 'area_de_servidao_administrativa' },
  { label: 'Área Rural Consolidada', field: 'area_rural_consolidada' },
  { label: 'Área de Pousio', field: 'area_de_pousio' },
];

export default function ReportPrintCarDetails({ snapshot }) {
  const { isDomainEnabled, isSectionEnabled, isFieldEnabled, getFieldLabel, getStyleForRole } =
    useReportConfig();

  if (!isDomainEnabled('car')) {
    return (
      <Box sx={{ mt: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Domínio CAR desabilitado na configuração do relatório.
        </Typography>
      </Box>
    );
  }

  const properties =
    snapshot && snapshot.properties && typeof snapshot.properties === 'object'
      ? snapshot.properties
      : {};

  const dataRaw = properties.car;
  const data = Array.isArray(dataRaw) ? dataRaw : dataRaw ? [dataRaw] : [];
  const sobreposicao = properties.sobreposicao;

  if (!Array.isArray(data) || data.length === 0) {
    return (
      <Box sx={{ mt: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Nenhum registro de CAR encontrado para este relatório.
        </Typography>
      </Box>
    );
  }

  const biomasChips = extractBiomas(sobreposicao);

  const renderField = (domainKey, fieldKey, label, value) => (
    <Box sx={{ mb: 1.25 }}>
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
        {label}
      </Typography>
      <ReportStyledField
        domainKey={domainKey}
        fieldKey={fieldKey}
        variant="body1"
        defaultSx={{ fontWeight: 500 }}
      >
        {value || '-'}
      </ReportStyledField>
    </Box>
  );

  const renderThemesTable = (themes, details, areaPropriedade) => (
    <Table
      size="small"
      sx={{
        backgroundColor: 'white',
      }}
    >
      <TableHead>
        <TableRow>
          <TableCell sx={{ fontWeight: 700 }}>Temas Ambientais</TableCell>
          <TableCell align="right" sx={{ fontWeight: 700 }}>
            ha
          </TableCell>
          <TableCell align="right" sx={{ fontWeight: 700 }}>
            %
          </TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {themes.map((theme, index) => {
          const areaValor = details?.[theme.field];
          const label = getFieldLabel('car', theme.field, theme.label);
          return (
            <TableRow key={theme.field} sx={{ bgcolor: index % 2 === 0 ? 'grey.50' : 'grey.100' }}>
              <TableCell>{label}</TableCell>
              <TableCell align="right">{formatArea(areaValor)}</TableCell>
              <TableCell align="right">{computePercent(areaValor, areaPropriedade)}</TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );

  const visibleLeftThemes = leftThemes.filter((theme) =>
    isFieldEnabled('car', 'temas_ambientais', theme.field),
  );
  const visibleRightThemes = rightThemes.filter((theme) =>
    isFieldEnabled('car', 'temas_ambientais', theme.field),
  );

  return (
    <Box sx={{ mt: 1 }}>
      <Typography variant="h6" sx={getStyleForRole('domainTitle', { mb: 2 })}>
        Cadastro Ambiental Rural (CAR)
      </Typography>

      {data.map((car, index) => {
        const details = car?.details || {};
        const areaPropriedade = details.area_da_propriedade;
        const reservaLegalStatus = classifyReservaLegalStatus(details, sobreposicao);

        return (
          <Box key={car?.cod_imovel || index} sx={{ mb: 3 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
              Código CAR: {car?.cod_imovel || '-'}
            </Typography>

            <Card
              variant="outlined"
              sx={{
                boxShadow: 1,
                borderRadius: 2,
                borderLeft: 4,
                borderLeftColor: 'success.main',
                borderLeftStyle: 'solid',
              }}
            >
              <CardContent>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={12} md={3}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontWeight: 600, display: 'block' }}
                    >
                      Status do Cadastro
                    </Typography>
                    <Box sx={{ mt: 0.5 }}>
                      {(() => {
                        const chipProps = getStatusChipProps(details.situacao_do_cadastro);
                        return <Chip size="small" {...chipProps} />;
                      })()}
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontWeight: 600, display: 'block' }}
                    >
                      Adesão ao PRA
                    </Typography>
                    <Box sx={{ mt: 0.5 }}>
                      {details.solicitacao_de_adesao_ao_pra ? (
                        <Chip
                          label={details.solicitacao_de_adesao_ao_pra}
                          size="small"
                          color={
                            String(details.solicitacao_de_adesao_ao_pra).toLowerCase() === 'sim'
                              ? 'success'
                              : 'default'
                          }
                        />
                      ) : (
                        <Typography variant="body2">-</Typography>
                      )}
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontWeight: 600, display: 'block' }}
                    >
                      Área da Propriedade
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {formatArea(details.area_da_propriedade)} ha
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontWeight: 600, display: 'block' }}
                    >
                      Reserva Legal
                    </Typography>
                    <Box sx={{ mt: 0.5 }}>
                      <Chip
                        size="small"
                        label={reservaLegalStatus.label}
                        color={reservaLegalStatus.color}
                        variant={reservaLegalStatus.label === 'Regular' ? 'outlined' : 'filled'}
                      />
                    </Box>
                  </Grid>
                </Grid>

                {isSectionEnabled('car', 'dados_cadastrais') && (
                  <ReportWidget sectionKey="car" widgetKey="car_dados_cadastrais">
                    <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                      <Typography
                        variant="subtitle2"
                        sx={getStyleForRole('sectionTitle', { mb: 1 })}
                      >
                        Dados cadastrais
                      </Typography>
                      <Grid container spacing={2}>
                        {isFieldEnabled('car', 'dados_cadastrais', 'uf') && (
                          <Grid item xs={12} md={1}>
                            {renderField('car', 'uf', getFieldLabel('car', 'uf', 'UF'), details.uf)}
                          </Grid>
                        )}
                        {isFieldEnabled('car', 'dados_cadastrais', 'municipio') && (
                          <Grid item xs={12} md={3}>
                            {renderField(
                              'car',
                              'municipio',
                              getFieldLabel('car', 'municipio', 'Município'),
                              details.municipio,
                            )}
                          </Grid>
                        )}
                        {isFieldEnabled('car', 'dados_cadastrais', 'area_da_propriedade') && (
                          <Grid item xs={12} md={2}>
                            {renderField(
                              'car',
                              'area_da_propriedade',
                              getFieldLabel(
                                'car',
                                'area_da_propriedade',
                                'Área da Propriedade (ha)',
                              ),
                              formatArea(details.area_da_propriedade),
                            )}
                          </Grid>
                        )}
                        {isFieldEnabled('car', 'dados_cadastrais', 'modulos_fiscais') && (
                          <Grid item xs={12} md={2}>
                            {renderField(
                              'car',
                              'modulos_fiscais',
                              getFieldLabel('car', 'modulos_fiscais', 'Módulos Fiscais'),
                              formatArea(details.modulos_fiscais),
                            )}
                          </Grid>
                        )}
                        {isFieldEnabled('car', 'dados_cadastrais', 'tipo_de_imovel_rural') && (
                          <Grid item xs={12} md={2}>
                            {renderField(
                              'car',
                              'tipo_de_imovel_rural',
                              getFieldLabel('car', 'tipo_de_imovel_rural', 'Tipo de Imóvel Rural'),
                              formatTipoImovelLabel(details.tipo_de_imovel_rural),
                            )}
                          </Grid>
                        )}
                        {isFieldEnabled('car', 'dados_cadastrais', 'condicao_do_cadastro') && (
                          <Grid item xs={12} md={2}>
                            {renderField(
                              'car',
                              'condicao_do_cadastro',
                              getFieldLabel('car', 'condicao_do_cadastro', 'Condição do Cadastro'),
                              details.condicao_do_cadastro,
                            )}
                          </Grid>
                        )}
                      </Grid>
                    </Box>
                  </ReportWidget>
                )}

                <Box sx={{ borderTop: 1, borderColor: 'divider', my: 1.5 }} />

                {isSectionEnabled('car', 'datas') && (
                  <ReportWidget sectionKey="car" widgetKey="car_datas">
                    <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                      <Grid container spacing={2}>
                        {isFieldEnabled('car', 'datas', 'data_de_inscricao') && (
                          <Grid item xs={12} md={3}>
                            {renderField(
                              'car',
                              'data_de_inscricao',
                              getFieldLabel('car', 'data_de_inscricao', 'Data da Inscrição'),
                              formatDateBr(details.data_de_inscricao),
                            )}
                          </Grid>
                        )}
                        {isFieldEnabled('car', 'datas', 'data_da_ultima_retificacao') && (
                          <Grid item xs={12} md={3}>
                            {renderField(
                              'car',
                              'data_da_ultima_retificacao',
                              getFieldLabel(
                                'car',
                                'data_da_ultima_retificacao',
                                'Última Retificação',
                              ),
                              formatDateBr(details.data_da_ultima_retificacao),
                            )}
                          </Grid>
                        )}
                        {isFieldEnabled(
                          'car',
                          'datas',
                          'data_de_alteracao_da_condicao_do_cadastro',
                        ) && (
                          <Grid item xs={12} md={3}>
                            {renderField(
                              'car',
                              'data_de_alteracao_da_condicao_do_cadastro',
                              getFieldLabel(
                                'car',
                                'data_de_alteracao_da_condicao_do_cadastro',
                                'Alteração da Condição do Cadastro',
                              ),
                              formatDateBr(details.data_de_alteracao_da_condicao_do_cadastro),
                            )}
                          </Grid>
                        )}
                        <Grid item xs={12} md={3}>
                          <Box sx={{ mb: 1.5 }}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}
                            >
                              Bioma
                            </Typography>
                            {biomasChips.length === 0 ? (
                              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                -
                              </Typography>
                            ) : (
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {biomasChips[0] && (
                                  <Chip
                                    size="small"
                                    label={formatBiomaChipLabel(
                                      biomasChips[0].bioma,
                                      biomasChips[0].pct,
                                    )}
                                    variant="outlined"
                                    color="default"
                                  />
                                )}
                                {biomasChips[1] && (
                                  <Chip
                                    size="small"
                                    label={formatBiomaChipLabel(
                                      biomasChips[1].bioma,
                                      biomasChips[1].pct,
                                    )}
                                    variant="outlined"
                                    color="default"
                                  />
                                )}
                                {biomasChips[2] && (
                                  <Chip
                                    size="small"
                                    label={formatBiomaChipLabel(
                                      biomasChips[2].bioma,
                                      biomasChips[2].pct,
                                    )}
                                    variant="outlined"
                                    color="default"
                                  />
                                )}
                              </Box>
                            )}
                          </Box>
                        </Grid>
                      </Grid>
                    </Box>
                  </ReportWidget>
                )}

                <Box sx={{ borderTop: 1, borderColor: 'divider', my: 1.5 }} />

                {isSectionEnabled('car', 'temas_ambientais') &&
                  (visibleLeftThemes.length > 0 || visibleRightThemes.length > 0) && (
                    <ReportWidget sectionKey="car" widgetKey="car_temas_ambientais">
                      <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                        <Typography
                          variant="subtitle2"
                          sx={getStyleForRole('sectionTitle', { mb: 1 })}
                        >
                          Temas ambientais (CAR)
                        </Typography>
                        <Grid container spacing={2}>
                          {visibleLeftThemes.length > 0 && (
                            <Grid item xs={12} md={6}>
                              {renderThemesTable(visibleLeftThemes, details, areaPropriedade)}
                            </Grid>
                          )}
                          {visibleRightThemes.length > 0 && (
                            <Grid item xs={12} md={6}>
                              {renderThemesTable(visibleRightThemes, details, areaPropriedade)}
                            </Grid>
                          )}
                        </Grid>
                      </Box>
                    </ReportWidget>
                  )}
              </CardContent>
            </Card>
          </Box>
        );
      })}
    </Box>
  );
}
